import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FilePlus2, FileText, Lock, PackageCheck, Loader2, X, AlertTriangle, Info, Check, CheckCircle2, ExternalLink, StickyNote } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtMb = (b) => (b || 0) < 1024 * 1024 ? `${Math.max(1, Math.round((b || 0) / 1024))} KB` : `${((b || 0) / (1024 * 1024)).toFixed(1)} MB`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* QC-TITLE-UPLOAD-001 — QC adds a PDF during Title QC Review (Internal Only | Include in Final Package). */
export const QcAddDocumentDialog = ({ orderId, open, onOpenChange, onUploaded }) => {
  const [budget, setBudget] = useState(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('internal');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null); setMode('internal'); setNote(''); setBudget(null);
    axios.get(`${API}/api/orders/${orderId}/qc-documents/budget`, { headers: authHeaders() })
      .then((r) => setBudget(r.data)).catch(() => setBudget({ error: true }));
  }, [open, orderId]);

  const budgetLoading = budget === null;
  const deliverableAllowed = !!budget?.deliverable_allowed;
  const isPortal = budget?.delivery_mode === 'external_portal';
  const overBudget = !isPortal && mode === 'final' && file && budget && !budget.error && file.size > budget.remaining_bytes;
  const isPdf = (f) => f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name));

  const pick = (f) => {
    if (!f) return;
    if (!isPdf(f)) { toast.error('QC documents must be PDF files.'); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error('File too large — QC documents are limited to 15 MB.'); return; }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'supporting_document');
      fd.append('qc_added', 'true');
      fd.append('qc_deliverable', mode === 'final' ? 'true' : 'false');
      if (note.trim()) fd.append('qc_note', note.trim());
      await axios.post(`${API}/api/orders/${orderId}/files/upload`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success(`Document added — ${mode === 'final' ? 'included in the final package' : 'internal only'}.`);
      window.dispatchEvent(new CustomEvent('orderFilesChanged', { detail: { orderId } }));
      onUploaded?.();
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  const [dragOver, setDragOver] = useState(false);
  const T = 'transition-[border-color,background-color,box-shadow,color,transform,opacity] duration-200 ease-out';
  const NOTE_MAX = 500; // mirrors the backend cap (qc_note[:500])

  const OptionCard = ({ selected, disabled, loading, icon, iconCls, title, desc, tip, badge, onSelect, children, testId }) => (
    <button type="button" role="radio" aria-checked={selected} disabled={disabled || loading} onClick={onSelect}
      className={`w-full text-left flex items-center gap-3.5 rounded-xl border px-3.5 py-3 ${T} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300
        ${selected ? 'border-[#2563eb] bg-blue-50/60 shadow-[0_0_0_1px_rgba(37,99,235,0.25)]' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}
        ${loading ? 'cursor-wait' : disabled ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'cursor-pointer'}`}
      data-testid={testId} data-selected={selected ? 'true' : 'false'} data-loading={loading ? 'true' : 'false'}>
      <span className={`h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 ${T} ${selected ? 'border-[#2563eb]' : 'border-slate-300'}`} aria-hidden="true">
        <span className={`h-2.5 w-2.5 rounded-full bg-[#2563eb] ${T} ${selected ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} />
      </span>
      <span className={`h-11 w-11 rounded-xl grid place-items-center shrink-0 ${iconCls}`}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-[15px] font-semibold text-slate-900">{title}
          {tip && <span title={tip} className="text-slate-400 hover:text-slate-600" data-testid={`${testId}-tip`}><Info className="w-3.5 h-3.5" /></span>}
        </span>
        <span className="block text-[13px] text-slate-500 leading-snug">{desc}</span>
        {children}
      </span>
      {badge}
      {selected && <CheckCircle2 className="w-5 h-5 text-[#2563eb] shrink-0 animate-in zoom-in-50 duration-200" aria-hidden="true" />}
    </button>
  );

  const portalTip = isPortal
    ? `This client is delivered via External Portal — the ${budget?.handoff_stage === 'qc_approve' ? 'QC approver' : 'Typist'} must confirm this document was uploaded to the client portal before the order can complete.`
    : 'Appended to the merged Title Report after the Primary Search Document: Final Search Report → Primary → QC documents.';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent hideCloseButton className="max-w-[640px] p-0 overflow-hidden rounded-2xl border border-slate-200/80 shadow-[0_24px_64px_-16px_rgba(15,23,42,0.35)] gap-0
          data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-200"
        data-testid="qc-add-document-dialog">
        {/* Header — subtle blue/indigo accent sweep */}
        <DialogHeader className="relative px-6 pt-6 pb-4 text-left overflow-hidden">
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 right-10 h-40 w-72 rotate-[-18deg] rounded-full bg-gradient-to-r from-blue-200/50 via-indigo-200/40 to-transparent blur-2xl" />
          <div className="relative flex items-start gap-4 pr-12">
            <span className="h-14 w-14 rounded-2xl grid place-items-center bg-gradient-to-br from-blue-50 to-indigo-50 text-[#2563eb] ring-1 ring-blue-100 shadow-sm"><FilePlus2 className="w-6 h-6" /></span>
            <div className="pt-1">
              <DialogTitle className="text-[22px] font-bold tracking-tight text-slate-900 leading-tight">Add Supporting Document</DialogTitle>
              <DialogDescription className="text-[14px] text-slate-500 mt-1">Upload a PDF document to this order.</DialogDescription>
            </div>
          </div>
          <button type="button" onClick={() => !busy && onOpenChange(false)} aria-label="Close" data-testid="qc-add-document-close"
            className={`absolute right-5 top-5 h-10 w-10 rounded-xl bg-slate-100 text-slate-500 grid place-items-center ${T} hover:bg-slate-200 hover:text-slate-800`}>
            <X className="w-4 h-4" />
          </button>
        </DialogHeader>

        <div className="px-6 pb-5 space-y-3">
          {/* Drop zone */}
          <button type="button" onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
            className={`group w-full rounded-2xl border-2 border-dashed px-4 text-center ${T} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300
              ${file ? 'py-5 border-emerald-300 bg-emerald-50/40' : `py-8 ${dragOver ? 'border-[#2563eb] bg-blue-50/70 shadow-[0_0_0_4px_rgba(37,99,235,0.12)]' : 'border-blue-200/80 bg-gradient-to-b from-slate-50/60 to-blue-50/30 hover:border-[#2563eb] hover:bg-blue-50/50'}`}`}
            data-testid="qc-add-document-dropzone" data-has-file={file ? 'true' : 'false'} data-drag-over={dragOver ? 'true' : 'false'}>
            <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="qc-add-document-input" />
            {file ? (
              <div className="flex items-center gap-3.5 text-left" data-testid="qc-add-document-selected">
                <span className="relative h-12 w-12 rounded-xl bg-white border border-emerald-200 grid place-items-center shadow-sm">
                  <FileText className="w-6 h-6 text-rose-500" />
                  <span className="absolute -right-1.5 -bottom-1.5 h-5 w-5 rounded-full bg-emerald-500 text-white grid place-items-center ring-2 ring-white animate-in zoom-in-50 duration-200"><Check className="w-3 h-3" /></span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold text-slate-900 truncate" title={file.name}>{file.name}</span>
                  <span className="block text-[12.5px] text-emerald-700 font-medium">{fmtMb(file.size)} · PDF ready to upload</span>
                </span>
                <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                  className={`h-8 px-3 inline-flex items-center rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 ${T} hover:bg-slate-50 hover:-translate-y-px`} data-testid="qc-add-document-replace">Replace</span>
                <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  className={`h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 ${T} hover:bg-rose-50 hover:text-rose-600`} aria-label="Remove file" data-testid="qc-add-document-clear"><X className="w-4 h-4" /></span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <span className={`relative mb-2 h-16 w-16 rounded-full bg-gradient-to-br from-blue-100/70 to-indigo-100/50 grid place-items-center ${T} group-hover:scale-105`}>
                  <span className="relative h-12 w-10 rounded-md bg-white border border-rose-200 shadow-sm grid place-items-end pb-1">
                    <span className="absolute inset-x-0 top-2 grid place-items-center"><FileText className="w-4 h-4 text-rose-400" /></span>
                    <span className="mx-auto rounded bg-rose-500 px-1 text-[9px] font-bold text-white leading-4">PDF</span>
                  </span>
                </span>
                <span className="text-[16px] font-semibold text-slate-800">Drop a PDF here or <span className="text-[#2563eb]">click to browse</span></span>
                <span className="text-[13px] text-slate-500">PDF only · Max 15 MB</span>
              </div>
            )}
          </button>

          {/* Visibility cards */}
          <div role="radiogroup" aria-label="Document visibility" className="space-y-2.5">
            <OptionCard testId="qc-doc-mode-internal" selected={mode === 'internal'} onSelect={() => setMode('internal')}
              icon={<Lock className="w-5 h-5" />} iconCls="bg-blue-50 text-[#2563eb]" title="Internal Only"
              desc="Retained with the order for QC / audit." tip="Not included in customer delivery." />
            <OptionCard testId="qc-doc-mode-final" selected={mode === 'final'} disabled={!budgetLoading && !deliverableAllowed} loading={budgetLoading}
              onSelect={() => deliverableAllowed && setMode('final')}
              icon={<PackageCheck className="w-5 h-5" />} iconCls="bg-emerald-50 text-emerald-600" title="Include in Final Package"
              desc={<span data-testid="qc-doc-final-description">Customer-facing document.</span>} tip={portalTip}
              badge={isPortal ? (
                <span className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-50 text-[#2563eb] text-[12.5px] font-semibold shrink-0" title={portalTip} data-testid="qc-doc-portal-badge">
                  <ExternalLink className="w-3.5 h-3.5" /> External Portal
                </span>) : null}>
              {budgetLoading && <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-400" data-testid="qc-doc-budget-loading"><Loader2 className="w-3 h-3 animate-spin" />Checking delivery package…</span>}
              {budget && !budget.error && !deliverableAllowed && (
                <span className="mt-1 flex items-start gap-1.5 text-xs text-amber-700" data-testid="qc-doc-final-blocked-reason"><Info className="w-3.5 h-3.5 mt-px shrink-0" />{budget.deliverable_blocked_reason}</span>
              )}
            </OptionCard>
          </div>

          {/* Package budget — only when relevant (unchanged rule) */}
          {budget && !budget.error && deliverableAllowed && !isPortal && mode === 'final' && (
            <div className={`rounded-xl px-3 py-2 text-xs flex items-start gap-2 ${overBudget ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-slate-50 text-slate-600 border border-slate-200'}`} data-testid="qc-doc-budget">
              {overBudget ? <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" /> : <Info className="w-3.5 h-3.5 mt-px shrink-0" />}
              <span>Package so far {fmtMb(budget.used_bytes)}{budget.estimate_complete ? '' : ' (report size not estimated)'} · remaining <b>{fmtMb(budget.remaining_bytes)}</b> of 15 MB.
                {overBudget && <> This PDF ({fmtMb(file.size)}) would exceed the 15 MB delivery limit — keep it Internal Only or reduce the file.</>}</span>
            </div>
          )}

          {/* Note */}
          <div>
            <label className="text-[15px] font-semibold text-slate-800" htmlFor="qc-doc-note">Note <span className="font-normal text-slate-500">(optional)</span></label>
            <div className={`mt-1.5 flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 ${T} focus-within:border-[#2563eb] focus-within:ring-2 focus-within:ring-blue-100`}>
              <StickyNote className="w-4 h-4 text-slate-400 mt-[3px] shrink-0" aria-hidden="true" />
              <textarea id="qc-doc-note" value={note} onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))} rows={note.length > 60 ? 2 : 1} placeholder="Add a note about this document..."
                className="flex-1 resize-none bg-transparent text-[14px] text-slate-800 placeholder:text-slate-400 focus:outline-none leading-5" data-testid="qc-doc-note" />
              <span className="text-[12px] text-slate-400 tabular-nums mt-[2px]" data-testid="qc-doc-note-counter">{note.length}/{NOTE_MAX}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/70 flex justify-end gap-2.5">
          <button type="button" onClick={() => onOpenChange(false)} disabled={busy}
            className={`h-11 px-5 rounded-xl border border-slate-200 bg-white text-[15px] font-semibold text-slate-700 shadow-sm ${T} hover:bg-slate-50 hover:-translate-y-px hover:shadow disabled:opacity-50`} data-testid="qc-add-document-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={!file || busy || overBudget} aria-busy={busy}
            className={`h-11 px-5 rounded-xl bg-[#2563eb] text-white text-[15px] font-semibold shadow-md shadow-blue-600/25 inline-flex items-center gap-2 ${T} hover:bg-[#1d4ed8] hover:-translate-y-px hover:shadow-lg disabled:opacity-50 disabled:pointer-events-none`} data-testid="qc-add-document-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}{busy ? 'Uploading…' : 'Add Document'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const QcAddDocumentButton = ({ orderId, onUploaded, className = '', compact = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        title="Add a supporting document and approve — no need to return the order to the Search Processor"
        className={compact
          ? `inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-blue-200 bg-white text-[12.5px] font-semibold text-blue-700 shadow-sm transition-[background-color,border-color,box-shadow,transform] duration-200 hover:bg-blue-50 hover:border-blue-300 hover:shadow hover:-translate-y-px ${className}`
          : `inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-blue-200 bg-white text-sm font-semibold text-blue-700 hover:bg-blue-50 transition-colors ${className}`}
        data-testid="qc-add-document-btn">
        <FilePlus2 className="w-4 h-4" />{compact ? 'Add Document' : 'Add Supporting Document'}
      </button>
      <QcAddDocumentDialog orderId={orderId} open={open} onOpenChange={setOpen} onUploaded={onUploaded} />
    </>
  );
};
